'use client'

import { ChangeEvent, useEffect, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { removeNonAlphabet } from '@/utils/string'
import {
  getAllProvince,
  getMapWilayahByParent,
  isProvince,
  MapWilayah,
  WILAYAH_NASIONAL,
} from '@/apis/data/statistik/wilayah'
import { District } from '@/apis/types'

import { WilayahSelectionProps } from './types'

export const useWilayahSelection = ({
  data,
  show,
  onClose,
  onSelect,
}: WilayahSelectionProps) => {
  const [keyword, setKeyword] = useDebounceValue('', 500)

  const [selectedProvince, setSelectedProvince] = useState<District | null>(
    null
  )
  const [selectedCity, setSelectedCity] = useState<District | null>(null)
  const [selectedText, setSelectedText] = useState<string>('Indonesia')

  const [wilayahMap, setWilayahMap] = useState<MapWilayah | null>(null)
  const [allProvincies, setAllProvincies] = useState<District[]>([])
  const [provincies, setProvincies] = useState<District[]>([])
  const [cities, setCities] = useState<District[]>([])

  const handleChangeKeyword = (event: ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value)
  }

  const handleSelectProvince = (province: District | null) => {
    setSelectedProvince(province)
    setSelectedText(province?.name || WILAYAH_NASIONAL.name)
    setSelectedCity(null)

    if (province) {
      const foundCity = wilayahMap?.[province.id].filter(
        (item: District) => item.parentId === province.id
      )
      // @ts-ignore
      setCities(foundCity)
    } else {
      setCities([])
    }
  }

  const handleSelectCity = (city: District) => {
    setSelectedCity(city)
    setSelectedText(city.name)
  }

  const handleClose = () => {
    setProvincies(allProvincies)
    setCities([])
    setKeyword('')
    setSelectedText('Indonesia')
    setSelectedCity(null)
    setSelectedProvince(null)
    onClose?.()
  }

  const handleSubmit = () => {
    if (selectedCity) {
      onSelect?.(selectedCity)
    } else if (selectedProvince) {
      onSelect?.(selectedProvince)
    } else {
      onSelect?.(WILAYAH_NASIONAL)
    }

    handleClose?.()
  }

  useEffect(() => {
    if (keyword) {
      const setOfProvinces = new Set()

      const filtered = data.filter((item) => {
        const isMatch = removeNonAlphabet(item.name)
          .toLowerCase()
          .includes(removeNonAlphabet(keyword).toLowerCase())

        if (isMatch && !isProvince(item)) {
          setOfProvinces.add(item.parentId)
        }

        if (isMatch && isProvince(item)) {
          setOfProvinces.add(item.id)
        }

        return isMatch
      })

      const filteredProv = allProvincies.filter((item) => {
        return setOfProvinces.has(item.id)
      })

      const theCities = filtered.filter((item) => !isProvince(item))
      setCities(theCities)
      setProvincies(filteredProv)
    } else {
      setProvincies(allProvincies)
      setCities([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword])

  useEffect(() => {
    if (show && data) {
      const p = getAllProvince(data)
      setProvincies(p)
      setAllProvincies(p)
      setWilayahMap(getMapWilayahByParent(data))
    }
  }, [show, data])

  return {
    handleChangeKeyword,
    handleSelectProvince,
    handleSelectCity,
    handleSubmit,
    handleClose,

    selectedText,
    selectedCity,
    selectedProvince,
    cities,
    provincies,
  }
}
